import { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CHART_INK, tone, useIsDarkMode } from '@/lib/chartTheme';
import { formatCompact } from '@/lib/money';

/** Figma Users Overview: Visitors yellow, Users blue, Creators pink/red. */
const SLICE_COLORS = {
  visitors: '#eab308',
  users: '#3b82f6',
  creators: '#f43f5e',
} as const;

export type AudienceSlice = {
  key: 'visitors' | 'users' | 'creators';
  label: string;
  value: number | null;
  comingSoon?: boolean;
};

/**
 * Figma right-rail Users Overview donut with center total.
 */
export function DashboardAudienceDonut({
  slices,
  isLoading,
}: {
  slices: AudienceSlice[];
  isLoading?: boolean;
}) {
  const isDark = useIsDarkMode();
  const live = slices.filter((s) => !s.comingSoon && s.value != null);
  const total = live.reduce((sum, s) => sum + (s.value ?? 0), 0);
  const visitorsSoon = slices.some((s) => s.key === 'visitors' && s.comingSoon);

  const option = useMemo(() => {
    const ink = {
      primary: tone(CHART_INK.primary, isDark),
      muted: tone(CHART_INK.muted, isDark),
      surface: tone(CHART_INK.surface, isDark),
      grid: tone(CHART_INK.grid, isDark),
    };
    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'item',
        backgroundColor: ink.surface,
        borderColor: ink.grid,
        textStyle: { color: ink.primary },
        formatter: (params: { name: string; value: number; percent: number }) =>
          `${params.name}: ${formatCompact(params.value)} (${params.percent.toFixed(1)}%)`,
      },
      series: [
        {
          type: 'pie',
          radius: ['45%', '75%'],
          avoidLabelOverlap: true,
          label: { show: false },
          data: live.map((s) => ({
            name: s.label,
            value: s.value ?? 0,
            itemStyle: { color: SLICE_COLORS[s.key] },
          })),
        },
      ],
      graphic: [
        {
          type: 'text',
          left: 'center',
          top: 'middle',
          style: {
            text: total > 0 ? formatCompact(total) : '—',
            fill: ink.primary,
            fontSize: 22,
            fontWeight: 700,
            textAlign: 'center',
          },
        },
      ],
    };
  }, [isDark, live, total]);

  return (
    <Card className="border-border/40 bg-[#1a1a1c] shadow-none">
      <CardHeader className="space-y-1 p-5 pb-2">
        <CardTitle className="text-sm font-medium text-foreground">Users Overview</CardTitle>
        <p className="text-[11px] text-muted-foreground">
          Audience mix
          {visitorsSoon ? ' · Visitors pending PostHog' : ''}
        </p>
      </CardHeader>
      <CardContent className="space-y-4 p-5 pt-2">
        {isLoading ? (
          <div className="flex h-[200px] items-center justify-center text-xs text-muted-foreground">
            Loading audience…
          </div>
        ) : live.length === 0 || total === 0 ? (
          <div className="flex h-[200px] items-center justify-center rounded-md border border-dashed text-xs text-muted-foreground">
            No audience data.
          </div>
        ) : (
          <>
            <ReactECharts option={option} notMerge style={{ height: 200, width: '100%' }} />
            <div className="grid gap-2.5">
              {slices.map((s) => {
                const liveSlice = !s.comingSoon && s.value != null;
                const pct =
                  liveSlice && total > 0 ? (((s.value ?? 0) / total) * 100).toFixed(0) : null;
                return (
                  <div key={s.key} className="flex items-center justify-between gap-2 text-xs">
                    <span className="flex min-w-0 items-center gap-2">
                      <span
                        className="size-2.5 shrink-0 rounded-full"
                        style={{ backgroundColor: SLICE_COLORS[s.key] }}
                        aria-hidden
                      />
                      <span className="truncate text-muted-foreground">{s.label}</span>
                    </span>
                    <span className="tabular-nums text-foreground">
                      {s.comingSoon
                        ? 'Soon'
                        : `${formatCompact(s.value ?? 0)}${pct != null ? ` · ${pct}%` : ''}`}
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
